import { useEffect, useState } from 'react';

import getUniqueCitiesFromLocations from 'utils/getUniqueCitiesFromLocations';
import getUniqueStatesFromLocations from 'utils/getUniqueStatesFromLocations';
import isArrayNotEmpty from 'utils/isArrayNotEmpty';
import storageKeys from 'data/storageKeys';
import { getClosestLocationsBySortingAlgo } from 'utils/booking/getClosestLocationsBySortingAlgo';
import { pushGTMUserLocationDeclined, pushGTMUserLocationGranted, pushGTMUserLocationPrompted } from '../../utils/gtm';
import { flagshipStoreExists, orderByFlagStore } from '../../utils/orderByFlagStore';
import { setLocalStorage } from '../../utils/setLocalStorage';
import useSearchLocationApi from './useSearchLocationApi';

export function useGeolocationInput(locations, sortingAlgorithm) {
  const [isLoading, setIsLoading] = useState(false);
  const [isGeoLoading, setIsGeoLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [options, setOptions] = useState([]);
  const [gotLocations, setGotLocations] = useState(false);
  const [error, setError] = useState(null);
  const [userPosition, setUserPosition] = useState({});
  const [isLocationAsked, setIsLocationAsked] = useState(false);
  const { results: searchResults, searchLocation } = useSearchLocationApi({ searchText, setIsLoading, setError });

  const handleSetGotLocation = () => {
    setGotLocations(!gotLocations);
  };
  const getOptions = () => {
    const allStates = getUniqueStatesFromLocations(locations)?.map(state => ({
      ...state,
      label: state.name,
      value: state.name,
      type: 'state',
    }));

    const allCities = getUniqueCitiesFromLocations(locations)?.map(city => ({
      ...city,
      label: city.name,
      value: city.name,
      type: 'city',
    }));

    const allUniqueLocations = [...allStates, ...allCities].filter(
      (value, index, allLocations) => index === allLocations.findIndex(location => location.id === value.id),
    );

    const allOptions = allUniqueLocations.sort((a, b) => a.name?.localeCompare(b.name));

    setOptions(allOptions);
  };

  const getLocations = position => {
    const allClosestLocations = getClosestLocationsBySortingAlgo(locations, position, sortingAlgorithm);

    if (isArrayNotEmpty(allClosestLocations)) {
      const optionsData = allClosestLocations?.map(location => ({
        ...location,
        value: location.code,
        label: location.name,
        subtitle: `${location.distanceInKm} km`,
        address: location.address,
        heading: location.heading,
        flagshipStoreLabel: location.flagshipStoreLabel,
        image: location.image,
      }));

      flagshipStoreExists(optionsData, sortingAlgorithm)
        ? setOptions(orderByFlagStore(optionsData))
        : setOptions(optionsData);
    }
  };

  const onError = error => {
    // tracking of the permission status for user location - 2
    pushGTMUserLocationDeclined();
    setIsGeoLoading(false);
    setIsLoading(false);
    setError(error.message);
  };

  const changePosition = async ({ coords }) => {
    // tracking of the permission status for user location - 3
    pushGTMUserLocationGranted();
    setIsLoading(true);
    setIsGeoLoading(true);
    const position = {
      latitude: coords.latitude,
      longitude: coords.longitude,
    };

    setUserPosition(position);
    setLocalStorage(storageKeys.userCoordinates, JSON.stringify(position));

    getLocations(position);
    handleSetGotLocation();
    setIsLoading(false);
    setIsGeoLoading(false);
    setError(null);
  };

  const getGeoLocation = () => {
    if (!isLocationAsked)
      if (navigator.permissions && navigator.permissions.query)
        //try permissions APIs first
        navigator.permissions.query({ name: 'geolocation' }).then(function (result) {
          // Will return ['granted', 'prompt', 'denied']
          const permission = result.state;
          // tracking of the permission status for user location - 1
          if (permission === 'prompt') pushGTMUserLocationPrompted();
          if (permission === 'denied') pushGTMUserLocationDeclined();
          // proceed with the permission status
          if (permission === 'granted' || permission === 'prompt') onGeoLocation();
        });
      else if (navigator.geolocation)
        //then Navigation APIs
        onGeoLocation();

    return false;
  };

  const onGeoLocation = () => {
    setError(null);
    setIsGeoLoading(true);
    setIsLoading(true);
    const geo = navigator.geolocation;

    geo.getCurrentPosition(changePosition, onError);

    if (!geo || gotLocations) {
      setIsGeoLoading(false);
      setError('Geolocation is not supported');
    }

    setIsLocationAsked(!isLocationAsked);
    return false;
  };

  useEffect(() => {
    if (!isArrayNotEmpty(locations)) return;

    getOptions();
  }, [locations]);

  useEffect(() => {
    if (!isArrayNotEmpty(searchResults)) {
      setIsGeoLoading(false);
      setIsLoading(false);
      return;
    }

    const location = searchResults[0]?.geometry?.location;

    const coords = {
      latitude: location.lat,
      longitude: location.lng,
    };

    changePosition({ coords });
    setIsGeoLoading(false);
    setIsLoading(false);
  }, [searchResults]);

  useEffect(() => {
    if (!isArrayNotEmpty(locations)) return;
    if (!searchText && userPosition.latitude) {
      getLocations(userPosition);
      return;
    }
    getOptions();
  }, [searchText]);

  return {
    isLoading,
    getGeoLocation,
    searchLocation,
    setSearchText,
    error,
    options,
    gotLocations,
    setGotLocations,
    isGeoLoading,
  };
}
